import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, switchMap, timer } from 'rxjs';

/** Long enough for the first step's own bootstrap — POS, show, products, cart — to have the network. */
const PRELOAD_DELAY_MS = 2000;
/** Ceiling on how long the idle request itself may wait once that delay has passed. */
const IDLE_TIMEOUT_MS = 2000;

/**
 * Fetches a lazy route's chunk ahead of the buyer, but only for routes marked
 * `data: { preload: true }` and only once the browser has gone quiet. A step nobody has reached yet
 * must not compete with the step that is on screen: unflagged routes — the subscription flow, the
 * order and password pages — are left to load when they are actually navigated to.
 */
@Injectable({ providedIn: 'root' })
export class NextStepPreloadStrategy implements PreloadingStrategy {
  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (!route.data?.['preload']) {
      return of(null);
    }

    return timer(PRELOAD_DELAY_MS).pipe(
      switchMap(() => this.whenIdle()),
      switchMap(() => load())
    );
  }

  private whenIdle(): Observable<void> {
    return new Observable<void>((subscriber) => {
      const done = () => {
        subscriber.next();
        subscriber.complete();
      };

      if (typeof window === 'undefined' || typeof window.requestIdleCallback !== 'function') {
        const handle = setTimeout(done, IDLE_TIMEOUT_MS);
        return () => clearTimeout(handle);
      }

      const handle = window.requestIdleCallback(done, { timeout: IDLE_TIMEOUT_MS });
      return () => window.cancelIdleCallback(handle);
    });
  }
}
